import AddressForm, { Address, AddressFields } from './AddressForm';
import type SelectInput from '../controls/SelectInput';
import type FormHandler from '../FormHandler';
import StateFilteredAddressInputChangeHandler from './StateFilteredAddressInputChangeHandler';
import { Country, State, StateFilterData } from './types';
import TextInput from "../controls/TextInput";

type AddressFieldHandlerConfig = {
  fields: AddressFields,
  countries: Country[],
  states: State[],
};

function parseInputValue(stringValue: string) {
  if (!stringValue) {
    return -1;
  }

  return parseInt(stringValue, 10);
}

export default class StateFilteredAddress extends AddressForm {
  countries: Country[];

  states: State[];

  countryId: number | undefined;

  static createForCustomFormFields($container: HTMLElement, formHandler: FormHandler, stateFilterData: StateFilterData) {
    return [...$container.querySelectorAll('[data-tn-address-id]')]
      .map(($el) => {
        const id = $el.getAttribute('data-tn-address-id')!;

        return new StateFilteredAddress(formHandler, {
          countries: stateFilterData.countries,
          states: stateFilterData.states,
          fields: {
            Street1: `${id}_address1`,
            Street2: `${id}_address2`,
            CountryId: `${id}_country`,
            StateId: `${id}_state`,
            PostalCode: `${id}_postal`,
            City: `${id}_city`,
          },
        },
        $container);
      });
  }

  constructor(formHandler: FormHandler, config: AddressFieldHandlerConfig, $container?: HTMLElement) {
    super(formHandler, config.fields, $container);

    this.countries = config.countries;
    this.states = config.states;

    [this.country, this.state]
      .forEach(({ input }) => {
        input.addChangeHandler(new StateFilteredAddressInputChangeHandler(input as SelectInput, this));
      });

    this.update();
  }

  getCountryData(id: number) {
    if (id > 0) {
      const result = this.countries.find((country) => country.id === id);

      if (result) {
        return result;
      }
    }

    return {
      id: -1,
      description: '',
      shortDescription: '',
      hasStates: false,
      isStateRequired: false,
      isCityRequired: false,
      isPostalCodeRequired: false,
      isoCode: '',
    };
  }

  update() {
    const currentCountryId = this.countryId;
    const newCountryId = parseInputValue(this.country.input.value);

    if (newCountryId !== currentCountryId) {
      const stateId = parseInputValue(this.state.input.value);
      const countryData = this.getCountryData(newCountryId);
      
      this.setCountryRequirements(countryData);
      this.resetValidationErrors();
      
      if (currentCountryId !== undefined) {
          this.postalCode.input.value = '';
      }

      const stateInput = this.state.input as SelectInput;
      if (countryData.hasStates) {
        const options = this.states
          .filter((state) => state.countryId === newCountryId)
          .map((state) => [state.description, state.id.toString(), state.id === stateId] as [string, string, boolean]);
        
        stateInput.setOptions(options, 'Select');
        this.state.enable();
        this.state.show();
      } else {
        stateInput.setOptions([]);
        this.state.hide();
        this.state.disable();
      }

      this.countryId = newCountryId;
    }
  }

  updateAddress(address: Address) {
    // Update country first
    this.country.input.value = address.countryId;

    this.street1.input.value = address.address1;
    this.city.input.value = address.city;
    this.state.input.value = address.state;
    this.postalCode.input.value = address.postalCode;

    if (this.street2) {
      this.street2.input.value = address.address2;
    }
  }
  
  setCountryRequirements(country: Country) {
      this.city.setIsRequired(country.isCityRequired);
      this.postalCode.setIsRequired(country.isPostalCodeRequired);
      this.state.setIsRequired(country.isStateRequired);
  }
  
  resetValidationErrors() {
      this.state.clearErrors();
      this.postalCode.clearErrors();
  }
}
